/**
 * Proximity Map Identity System — palette + helpers.
 *
 * The single source-of-truth for every shape and color a contact can be
 * displayed as. Keep this list additive-only so existing DB rows never
 * become invalid.
 */

// Shapes drawn as inline SVG. Each function returns an SVG string that
// paints its silhouette at 100×100 in the given fill color, with a
// consistent 8px white stroke for map legibility.
export const SHAPES = [
  "circle", "triangle", "square", "star", "diamond", "x", "dollar", "heart",
];

export const SHAPE_LABELS = {
  circle: "Circle", triangle: "Triangle", square: "Square", star: "Star",
  diamond: "Diamond", x: "X", dollar: "$", heart: "Heart",
};

// Named colors + hex values. First value is the display, second is the fill.
export const COLORS = [
  { key: "red",       label: "Red",        hex: "#FF3B30" },
  { key: "orange",    label: "Orange",     hex: "#FF9500" },
  { key: "yellow",    label: "Yellow",     hex: "#FFCC00" },
  { key: "green",     label: "Green",      hex: "#34C759" },
  { key: "royalblue", label: "Royal Blue", hex: "#007AFF" },
  { key: "gold",      label: "Gold",       hex: "#D4AF37" },
  { key: "purple",    label: "Purple",     hex: "#AF52DE" },
  { key: "black",     label: "Black",      hex: "#111111" },
  { key: "white",     label: "White",      hex: "#F5F5F5" },
];

export const COLOR_HEX = Object.fromEntries(COLORS.map(c => [c.key, c.hex]));

/** Family default appearance. */
export const FAMILY_DEFAULT = { map_shape: "heart", map_color: "red" };

/** Random identity for a new non-family contact. */
export function randomIdentity() {
  return {
    map_shape: SHAPES[Math.floor(Math.random() * SHAPES.length)],
    map_color: COLORS[Math.floor(Math.random() * COLORS.length)].key,
  };
}

/**
 * Return an SVG string drawing the requested shape at 100×100 with a
 * white outline for legibility at any zoom.
 */
export function svgFor(shape, color, size = 100) {
  const fill = COLOR_HEX[color] || COLOR_HEX.royalblue;
  const stroke = "#ffffff";
  const sw = Math.max(4, Math.round(size * 0.08));
  const s = size, mid = s / 2;
  const paths = {
    circle:   `<circle cx="${mid}" cy="${mid}" r="${mid - sw}" fill="${fill}" stroke="${stroke}" stroke-width="${sw}"/>`,
    triangle: `<polygon points="${mid},${sw} ${s - sw},${s - sw} ${sw},${s - sw}" fill="${fill}" stroke="${stroke}" stroke-width="${sw}" stroke-linejoin="round"/>`,
    square:   `<rect x="${sw}" y="${sw}" width="${s - 2 * sw}" height="${s - 2 * sw}" rx="${sw / 2}" fill="${fill}" stroke="${stroke}" stroke-width="${sw}"/>`,
    star:     `<polygon points="${mid},${sw} ${mid + 18},${mid - 8} ${s - sw},${mid - 8} ${mid + 26},${mid + 14} ${mid + 34},${s - sw} ${mid},${s - 22} ${mid - 34},${s - sw} ${mid - 26},${mid + 14} ${sw},${mid - 8} ${mid - 18},${mid - 8}" fill="${fill}" stroke="${stroke}" stroke-width="${sw}" stroke-linejoin="round"/>`,
    diamond:  `<polygon points="${mid},${sw} ${s - sw},${mid} ${mid},${s - sw} ${sw},${mid}" fill="${fill}" stroke="${stroke}" stroke-width="${sw}" stroke-linejoin="round"/>`,
    x:        `<g stroke="${fill}" stroke-width="${sw * 2}" stroke-linecap="round" fill="none"><line x1="${sw * 2}" y1="${sw * 2}" x2="${s - sw * 2}" y2="${s - sw * 2}"/><line x1="${s - sw * 2}" y1="${sw * 2}" x2="${sw * 2}" y2="${s - sw * 2}"/></g><g stroke="${stroke}" stroke-width="${sw / 2}" stroke-linecap="round" fill="none"><line x1="${sw * 2}" y1="${sw * 2}" x2="${s - sw * 2}" y2="${s - sw * 2}"/><line x1="${s - sw * 2}" y1="${sw * 2}" x2="${sw * 2}" y2="${s - sw * 2}"/></g>`,
    dollar:   `<circle cx="${mid}" cy="${mid}" r="${mid - sw}" fill="${fill}" stroke="${stroke}" stroke-width="${sw}"/><text x="${mid}" y="${mid + 18}" text-anchor="middle" font-family="'Chivo',sans-serif" font-weight="900" font-size="60" fill="${stroke}">$</text>`,
    heart:    `<path d="M${mid} ${s - sw * 2} C${sw * 2} ${mid + 15}, ${sw * 2} ${sw * 3}, ${mid} ${mid - 5} C${s - sw * 2} ${sw * 3}, ${s - sw * 2} ${mid + 15}, ${mid} ${s - sw * 2} Z" fill="${fill}" stroke="${stroke}" stroke-width="${sw}" stroke-linejoin="round"/>`,
  };
  return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${s} ${s}" width="${s}" height="${s}">${paths[shape] || paths.circle}</svg>`;
}

/** Same as svgFor but returns a data-URL (for <img src=...>). */
export function svgDataUrl(shape, color, size = 100) {
  return `data:image/svg+xml;utf8,${encodeURIComponent(svgFor(shape, color, size))}`;
}

/** Given a contact object, resolve its display identity with sensible fallbacks. */
export function identityFor(contact) {
  const shape = contact?.map_shape || (contact?.is_family ? "heart" : "circle");
  const color = contact?.map_color || (contact?.is_family ? "red" : "royalblue");
  const displayName = contact?.display_name || contact?.nickname || contact?.name || "";
  return { shape, color, displayName };
}
